import type { CharacterSummary } from '../types';
import { CharacterCard } from '../components/CharacterCard';
import { AccountTitleCard } from '../components/AccountTitleCard';

interface Props {
  chars: CharacterSummary[];
  onOpenAccount: (ownerKey: string) => void;
  onOpenCharacter: (key: string) => void;
  onAddNew: () => void;
}

interface OwnerBucket {
  owner: string;       // display label
  ownerKey: string;    // raw value (empty string for legacy)
  chars: CharacterSummary[];
}

function groupByOwner(list: CharacterSummary[]): OwnerBucket[] {
  const map = new Map<string, OwnerBucket>();
  for (const c of list) {
    const key = c.owner ?? '';
    const label = c.owner ?? 'Legacy / shared';
    let g = map.get(key);
    if (!g) { g = { owner: label, ownerKey: key, chars: [] }; map.set(key, g); }
    g.chars.push(c);
  }
  return Array.from(map.values()).sort((a, b) => {
    // legacy/shared always last
    if (!a.ownerKey && b.ownerKey) return 1;
    if (a.ownerKey && !b.ownerKey) return -1;
    // then by recency of any character on the account, then by count
    const recencyA = Math.max(...a.chars.map((c) => c.updatedAt));
    const recencyB = Math.max(...b.chars.map((c) => c.updatedAt));
    if (recencyB !== recencyA) return recencyB - recencyA;
    return b.chars.length - a.chars.length;
  });
}

/**
 * Landing page. Shows the most recent uploads as compact pills, then a
 * grid of one big card per owning account. Clicking an account dives
 * into its character list (AccountView); clicking a recent-pill jumps
 * straight to that character.
 */
export function Dashboard({ chars, onOpenAccount, onOpenCharacter, onAddNew }: Props) {
  const buckets = groupByOwner(chars);

  // Recent activity: up to 6 most-recently-updated across all accounts.
  const recent = [...chars]
    .sort((a, b) => b.updatedAt - a.updatedAt)
    .slice(0, 6);

  if (chars.length === 0) {
    return (
      <div className="dashboard empty">
        <div className="empty-card">
          <h1 className="empty-title">HeroExtract</h1>
          <p className="empty-lede">Live character display for WotLK 3.3.5.</p>
          <p className="empty-sub">No character has been uploaded yet.</p>
          <a
            className="empty-cta"
            href="#add"
            onClick={(e) => { e.preventDefault(); onAddNew(); }}
          >
            Add your first character →
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <section className="dashboard-section">
        <h2 className="dashboard-section-title">Recent activity</h2>
        <div className="dashboard-recent-strip">
          {recent.map((c) => (
            <CharacterCard
              key={c.key}
              char={c}
              compact
              onClick={() => onOpenCharacter(c.key)}
            />
          ))}
        </div>
      </section>

      <section className="dashboard-section">
        <div className="dashboard-section-head">
          <h2 className="dashboard-section-title">Accounts</h2>
          <span className="dashboard-section-sub">{buckets.length} account{buckets.length === 1 ? '' : 's'} · {chars.length} character{chars.length === 1 ? '' : 's'} total</span>
        </div>
        <div className="dashboard-accounts-grid">
          {buckets.map((b) => (
            <AccountTitleCard
              key={b.ownerKey || '__legacy__'}
              owner={b.owner}
              ownerKey={b.ownerKey}
              chars={b.chars}
              onClick={() => onOpenAccount(b.ownerKey)}
            />
          ))}
        </div>
      </section>
    </div>
  );
}
